import React from 'react';
import { Link } from 'react-router-dom';
import { FaBullseye, FaUsers, FaBolt, FaShieldAlt, FaCheck, FaMusic, FaBuilding, FaGlobe, FaTheaterMasks, FaChurch, FaCamera, FaHotel, FaDice, FaFootballBall, FaTrophy, FaShip, FaGraduationCap, FaMicrophone } from 'react-icons/fa';
import SEOHead from '../components/SEOHead';

const MARKETS = [
  { name: 'Las Vegas', slug: 'las-vegas' },
  { name: 'Los Angeles', slug: 'los-angeles' },
  { name: 'New York', slug: 'new-york' },
  { name: 'Nashville', slug: 'nashville' },
  { name: 'Chicago', slug: 'chicago' },
  { name: 'Orlando', slug: 'orlando' },
  { name: 'Atlanta', slug: 'atlanta' },
  { name: 'Dallas', slug: 'dallas' },
  { name: 'Austin', slug: 'austin' },
  { name: 'Miami', slug: 'miami' },
  { name: 'San Francisco', slug: 'san-francisco' },
  { name: 'Denver', slug: 'denver' },
  { name: 'Phoenix', slug: 'phoenix' },
  { name: 'San Diego', slug: 'san-diego' },
  { name: 'Seattle', slug: 'seattle' },
  { name: 'Orange County', slug: 'orange-county' },
  { name: 'Anaheim', slug: 'anaheim' },
  { name: 'Houston', slug: 'houston' },
];

const INDUSTRIES = [
  { name: 'Concerts & Tours', icon: FaMusic },
  { name: 'Corporate Events', icon: FaBuilding },
  { name: 'Trade Shows', icon: FaGlobe },
  { name: 'Festivals', icon: FaMicrophone },
  { name: 'Film & TV', icon: FaCamera },
  { name: 'Theater', icon: FaTheaterMasks },
  { name: 'Church Events', icon: FaChurch },
  { name: 'Hotel AV', icon: FaHotel },
  { name: 'Casino Events', icon: FaDice },
  { name: 'Sports Events', icon: FaFootballBall },
  { name: 'Awards Shows', icon: FaTrophy },
  { name: 'Cruise Ship Events', icon: FaShip },
  { name: 'University Events', icon: FaGraduationCap },
  { name: 'Brand Activations', icon: FaBolt },
];

const AboutPage: React.FC = () => {
  return (
    <>
      <SEOHead
        title="About StageCrew — AV & Stagehand Gig Marketplace"
        description="StageCrew connects AV technicians and stagehands with event production companies through sourced listings, worker profiles, and preference-based matching."
        url="https://stagecrew.app/about"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 text-white py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-6">
            Built for Crew, by Crew
          </h1>
          <p className="text-lg md:text-xl opacity-90 leading-relaxed">
            StageCrew connects AV technicians and stagehands with event production companies.
            Workers can review current paid listings with clear source details, while employers can post complete crew calls directly.
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-gray-900 py-8 px-4">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div>
            <div className="text-3xl font-bold text-white">{MARKETS.length}</div>
            <div className="text-sm text-gray-400">Featured Market Guides</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-white">Role + City</div>
            <div className="text-sm text-gray-400">Preference Matching</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-white">{INDUSTRIES.length}</div>
            <div className="text-sm text-gray-400">Event Categories</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-white">Free</div>
            <div className="text-sm text-gray-400">For Workers</div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            The live events industry runs on relationships and word-of-mouth. Finding crew
            last minute often means checking scattered posts, texting old contacts, and
            hoping someone is available. For freelance crew, it can mean comparing listings
            that omit pay or no longer make clear whether applications are open.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            StageCrew fixes this. We built a platform specifically for the live event
            production industry — audio engineers, lighting techs, stagehands, riggers,
            video operators, and everyone who makes events happen behind the scenes.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            StageCrew combines listings from supported employer-controlled job boards with
            gigs posted directly by employers. Saved roles, locations, rates, and availability help surface
            relevant opportunities. Workers can apply manually or use optional automation
            where a posting destination supports it, while employers can review matched
            profiles and contact candidates directly.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="bg-gray-50 py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">What We Stand For</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FaBullseye className="text-blue-600 text-xl" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Niche Focus</h3>
                <p className="text-gray-600">
                  StageCrew is designed around AV, staging, film, and live-event crew
                  roles. Each sourced listing keeps its original source and application path.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <FaUsers className="text-green-600 text-xl" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Crew First</h3>
                <p className="text-gray-600">
                  Free for workers to browse and apply. We believe crew should have
                  access to available opportunities without paying to job search.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <FaBolt className="text-yellow-600 text-xl" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Preferences Matter</h3>
                <p className="text-gray-600">
                  Matching considers the role, location, rate, schedule, and preferences
                  available in a gig and worker profile. Match and fill times vary.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <FaShieldAlt className="text-purple-600 text-xl" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Direct Workflow</h3>
                <p className="text-gray-600">
                  StageCrew does not employ or dispatch workers. Employers and workers
                  review the details, verify requirements, and agree on the engagement directly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* StageCrew capabilities */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">How StageCrew Works</h2>
          <p className="text-gray-600 mb-8 text-center">
            A transparent marketplace workflow, with no promise that a specific gig will match, fill, or accept an application.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="text-left py-3 px-4 bg-gray-100 rounded-tl-lg font-bold">Capability</th>
                  <th className="text-left py-3 px-4 bg-blue-600 text-white rounded-tr-lg font-bold">What it means</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {[
                  ['Listings', 'Sourced and employer-posted crew opportunities'],
                  ['Matching', 'Compares available gig details with worker profiles and saved preferences'],
                  ['Worker access', 'Free browsing, profiles, saved jobs, and manual applications'],
                  ['Paid automation', 'Optional submission attempts on supported posting destinations'],
                  ['Employer workflow', 'Post gigs, review relevant profiles, and manage candidate statuses'],
                  ['Listing details', 'Original source and application path remain visible'],
                  ['Worker profiles', 'Worker-provided skills, experience, certifications, and availability'],
                  ['Communication', 'Employers contact candidates directly through available workflows'],
                ].map(([feature, detail], i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="py-3 px-4 font-medium text-gray-900">{feature}</td>
                    <td className="py-3 px-4 text-left text-blue-700">
                      <FaCheck className="inline text-green-500 mr-1" /> {detail}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="bg-gray-50 py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Industries We Serve</h2>
          <p className="text-gray-600 mb-8 text-center">StageCrew organizes crew opportunities across a range of live-event categories.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {INDUSTRIES.map(({ name, icon: Icon }) => (
              <div key={name} className="flex items-center gap-2 px-3 py-3 bg-white rounded-lg shadow-sm text-sm font-medium text-gray-700">
                <Icon className="text-blue-600 flex-shrink-0" />
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Active Markets */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Active Markets</h2>
          <p className="text-gray-600 mb-8">
            StageCrew is live and growing in major event production hubs across the US.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {MARKETS.map(({ name, slug }) => (
              <Link
                key={slug}
                to={`/gigs/${slug}`}
                className="px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium hover:bg-blue-100 transition-colors"
              >
                {name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="bg-gray-50 py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">The Team</h2>
          <p className="text-lg text-gray-700 mb-4">
            StageCrew was founded by <strong>Edwin Grace</strong> to solve a problem he saw
            firsthand — skilled AV professionals and stagehands struggling to find consistent
            work, while production companies scrambled to fill last-minute crew calls.
          </p>
          <p className="text-gray-600 mb-6">
            We're building the platform we wish existed when we were crewing up for shows.
          </p>
          <a
            href="mailto:edwin@stagecrew.app"
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            edwin@stagecrew.app
          </a>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              {
                q: 'How fast can I get matched with crew?',
                a: 'StageCrew compares each gig with active worker profiles and saved preferences. Match timing and availability depend on the workers who align with that specific role, location, rate, and schedule, so StageCrew does not promise a fill time.',
              },
              {
                q: 'What does auto-apply do?',
                a: 'Application automation is available through the $9 monthly Automation Early Access plan. It uses your saved preferences and attempts submission only where the posting destination supports it; you can pause it or choose review-first mode.',
              },
              {
                q: 'Is StageCrew really free for workers?',
                a: 'Yes. Browsing gigs, creating a profile, matching, and applying manually are completely free. Optional application automation is available through the $9 monthly Automation Early Access plan.',
              },
              {
                q: 'How do you vet crew members?',
                a: 'Workers provide their own skills, certifications, experience, and availability. StageCrew does not represent every profile detail as independently verified, so employers should review profiles, ask for supporting information when needed, and confirm requirements directly.',
              },
              {
                q: 'What areas does StageCrew cover?',
                a: 'StageCrew publishes market guides across the United States, including Las Vegas, Los Angeles, New York, Nashville, Chicago, Orlando, Atlanta, Dallas, Austin, Miami, San Francisco, and Denver. Live listing volume varies by market and date.',
              },
              {
                q: 'How is StageCrew different from staffing agencies?',
                a: 'StageCrew is a self-service marketplace, not a staffing agency or employer of record. It organizes sourced and employer-posted gigs, uses profile preferences for matching, and lets employers and workers verify details and communicate directly.',
              },
              {
                q: 'Can I post gigs for free?',
                a: 'Yes — your first 10 gig posts are free. After that, employer plans start at $99/mo for 5 monthly posts, worker matching, an employer dashboard, and a company profile.',
              },
              {
                q: 'What types of events does StageCrew cover?',
                a: 'Concerts, corporate events, trade shows, festivals, film/TV, theater, church events, hotel AV, casino events, sports events, awards shows, brand activations, and more.',
              },
            ].map(({ q, a }, i) => (
              <details key={i} className="group bg-white rounded-lg shadow-sm border border-gray-200">
                <summary className="flex items-center justify-between cursor-pointer px-6 py-4 font-medium text-gray-900 list-none">
                  {q}
                  <span className="text-gray-400 group-open:rotate-180 transition-transform">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </span>
                </summary>
                <p className="px-6 pb-4 text-gray-600">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-blue-600 text-white py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-lg opacity-90 mb-8">
            Whether you're crew looking for gigs or a company looking for crew,
            StageCrew gives you a focused place to start.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link
              to="/for-workers"
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-blue-50 transition-colors"
            >
              I'm Looking for Gigs
            </Link>
            <Link
              to="/for-employers"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white/10 transition-colors"
            >
              I'm Hiring Crew
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
